import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, User, ArrowLeft, Loader2, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const { register } = useAuth();
  const { t, getLocalizedPath } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for pending email address from landing page
    const pending = sessionStorage.getItem('pendingEmailAddress');
    if (pending) {
      try {
        setPendingEmail(JSON.parse(pending));
      } catch (e) {
        sessionStorage.removeItem('pendingEmailAddress');
      }
    }
  }, []);

  const claimPendingEmail = async (token) => {
    if (!pendingEmail) return;
    
    try {
      await axios.post(`${API}/emails/claim`, {
        email_id: pendingEmail.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Address ${pendingEmail.address} added to your account!`);
    } catch (error) {
      console.error('Failed to claim email:', error);
    } finally {
      sessionStorage.removeItem('pendingEmailAddress');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !username) {
      toast.error(t('auth.fillAllFields'));
      return;
    }
    
    if (password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      const userData = await register(email, password, username);
      toast.success(t('auth.register.success'));
      
      // Claim pending email if exists
      const token = localStorage.getItem('token');
      if (pendingEmail && token) {
        await claimPendingEmail(token);
      }
      
      navigate(getLocalizedPath('/dashboard'));
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title={t('auth.register.title')}
        description="Sign up for Instant-Email and get free temporary disposable email addresses. Create an account to save and manage multiple addresses."
        path="/register"
      />
      <div className="min-h-screen bg-background flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      <div className="w-full max-w-md relative z-10">
        <Link to={getLocalizedPath('/')} className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          {t('nav.backToHome')}
        </Link>
        
        <Card className="bg-card border-border">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <User className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="font-heading text-2xl">{t('auth.register.title')}</CardTitle>
            <CardDescription>{t('auth.register.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            {pendingEmail && (
              <div className="mb-6 p-4 rounded-lg bg-primary/10 border border-primary/30">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">{t('auth.login.emailReady')}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('nav.backToHome')}, <span className="font-mono text-foreground">{pendingEmail.address}</span> {t('auth.login.emailWillBeAdded')}
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t('auth.register.username')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="johndoe"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-input/50 border-transparent focus:border-primary"
                    data-testid="register-username-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.register.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-input/50 border-transparent focus:border-primary"
                    data-testid="register-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.register.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-input/50 border-transparent focus:border-primary"
                    data-testid="register-password-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">{t('auth.register.passwordHint')}</p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full rounded-full bg-primary text-primary-foreground hover:scale-[1.02] active:scale-[0.98] transition-all"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('auth.register.creating')}
                  </>
                ) : (
                  t('auth.register.submit')
                )}
              </Button>
            </form>
            
            <p className="text-center text-sm text-muted-foreground mt-6">
              {t('auth.register.haveAccount')}{' '}
              <Link to={getLocalizedPath('/login')} className="text-primary hover:underline" data-testid="go-to-login-link">
                {t('auth.register.signIn')}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default Register;
